// The product's top bar as plain HTML and CSS, for a build that has no
// component runtime.
//
// The web app renders the same bar from Svelte with utility classes; this
// renders it into static files. The two must look the same to the pixel,
// because a reader who clicks into the documentation has not left the product
// and a bar that changes under them says they have. So every value below was
// read off the computed style of the running web app rather than guessed from
// the utility names: the colours are the ones the palette resolves to, the
// spacing is the pixels the browser laid out, and the one breakpoint is the
// media query the utilities compile to.
//
// What is shared is the link set (./links.ts) — that is the part that would
// otherwise drift. The two stylesheets are held together by the parity test in
// apps/web/src/lib/nav.

import { BRAND, DOCS_LINK_ID, type NavLink, publicNavLinks, SIGNED_OUT_LINKS } from "./links.ts";

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * The brand mark, as the `d` of each stroked path on a 24×24 grid.
 *
 * Shared as data rather than as markup so both renderers draw the same shape
 * while each writes the element the way its own template system does.
 */
export const BRAND_MARK_PATHS: readonly string[] = [
  "M6 3h8l4 4v14H6z",
  "M14 3v4h4",
  "M9 13.5l2 2 4-4",
];

/** The bar's height in pixels: its vertical padding plus its tallest child. */
export const SITE_HEADER_HEIGHT_PX = 69;

/**
 * The bar's stylesheet, for a page that assembles its own `<style>`.
 *
 * It carries no position of its own — where the bar sits, and whether it stays
 * in view, is the surrounding page's decision — but it does publish its height
 * as `--site-header-height`, which a layout that has to reserve the space needs
 * and cannot measure.
 */
export const SITE_HEADER_STYLE = `
:root { --site-header-height: ${SITE_HEADER_HEIGHT_PX}px; }
/* The inherited half of the bar is declared as well as the visible half: the
   page this is dropped into has a body font of its own, and a child added to
   the bar later would pick that up instead of the bar's. */
.site-header { box-sizing: border-box; background: #fff;
  border-bottom: 1px solid oklch(92.9% 0.013 255.508);
  color: oklch(20.8% 0.042 265.755); font-size: 16px; line-height: 24px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue",
    "Noto Sans", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol",
    "Noto Color Emoji"; }
.site-header *, .site-header *::before, .site-header *::after { box-sizing: border-box; }
.site-header__inner { max-width: 1152px; margin-inline: auto; display: flex; flex-wrap: wrap;
  align-items: center; column-gap: 16px; row-gap: 12px; padding: 16px 24px; }
.site-header__brand { display: flex; align-items: center; gap: 8px; text-decoration: none;
  font-size: 14px; line-height: 20px; font-weight: 600; letter-spacing: -0.025em;
  color: oklch(20.8% 0.042 265.755); }
.site-header__brand svg { width: 24px; height: 24px; }
/* Below the one breakpoint the bar has, the sections drop to a full-width row
   of their own under the brand and the account controls, which is what the
   utility classes on the web app's header compile to. There is no menu to open:
   the row wraps, so every section stays one tap away and the bar needs no
   script to be usable. */
.site-header__nav { order: 9999; display: flex; width: 100%; flex-wrap: wrap;
  align-items: center; gap: 4px; }
.site-header__link { padding: 6px 12px; border-radius: 6px; text-decoration: none;
  font-size: 14px; line-height: 20px; font-weight: 500; color: oklch(44.6% 0.043 257.281); }
.site-header__link[aria-current="page"] { background: oklch(20.8% 0.042 265.755); color: #fff; }
.site-header__account { display: flex; align-items: center; gap: 8px; margin-left: auto; }
.site-header__cta { padding: 8px 16px; border-radius: 6px; text-decoration: none;
  font-size: 14px; line-height: 20px; font-weight: 500;
  background: oklch(20.8% 0.042 265.755); color: #fff; }
@media (hover: hover) {
  .site-header__link:not([aria-current="page"]):hover { background: oklch(96.8% 0.007 247.896);
    color: oklch(20.8% 0.042 265.755); }
  .site-header__cta:hover { background: oklch(37.2% 0.044 257.287); }
}
@media (width >= 40rem) {
  .site-header__nav { order: 0; width: auto; flex: 1; }
  .site-header__account { margin-left: 0; }
}
/* Where the pointer is a finger, every control in this bar is at least 44px in
   both directions – the vendors' own guidance for a thumb, and a stricter
   number than the 24px conformance line. It is scoped to a coarse pointer
   rather than applied everywhere so the bar a mouse sees is unchanged: this is
   about what a finger can land on, not about how the bar should look. The web
   app's own copy of this bar carries the identical rule, as the .tap-target
   class in apps/web/src/app.css, because the two are one bar rendered by two
   builds. */
@media (pointer: coarse) {
  .site-header__brand, .site-header__link, .site-header__cta {
    display: inline-flex; align-items: center; justify-content: center;
    min-height: 44px; min-width: 44px;
  }
  .site-header__brand { justify-content: flex-start; }
}
`;

export interface SiteHeaderInput {
  /** Where the documentation is mounted, as seen from the page being written. */
  readonly docsHref: string;
  /** The entry to mark as the page the reader is on, by its id. */
  readonly currentId: string | null;
}

function renderLink(link: NavLink, currentId: string | null): string {
  const current = link.id === currentId ? ' aria-current="page"' : "";
  const testid = link.id === DOCS_LINK_ID ? ' data-testid="docs-link"' : "";
  return `<a class="site-header__link" href="${escapeHtml(link.href)}"${testid}${current}>${escapeHtml(link.label)}</a>`;
}

/**
 * The bar, as one element ready to drop above a page's own content.
 *
 * It is marked out of the search index: a bar repeated on every page matches
 * every query on every page and buries the page that answers it, which is the
 * same reason the documentation's sidebar and footer carry the attribute.
 */
export function renderSiteHeader(input: SiteHeaderInput): string {
  const mark = BRAND_MARK_PATHS.map((d) => `<path d="${d}" />`).join("");
  const links = publicNavLinks(input.docsHref)
    .map((link) => renderLink(link, input.currentId))
    .join("");
  return `<header class="site-header" data-pagefind-ignore>
      <div class="site-header__inner">
        <a class="site-header__brand" href="${escapeHtml(BRAND.href)}" data-testid="nav-brand">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${mark}</svg>
          ${escapeHtml(BRAND.label)}
        </a>
        <nav class="site-header__nav" aria-label="Primary">${links}</nav>
        <div class="site-header__account">
          <a class="site-header__link" href="${escapeHtml(SIGNED_OUT_LINKS.signIn.href)}" data-testid="nav-signin">${escapeHtml(SIGNED_OUT_LINKS.signIn.label)}</a>
          <a class="site-header__cta" href="${escapeHtml(SIGNED_OUT_LINKS.register.href)}" data-testid="nav-register">${escapeHtml(SIGNED_OUT_LINKS.register.label)}</a>
        </div>
      </div>
    </header>`;
}
